import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, LogOut, Calendar, MapPin, Edit3, Loader2, Mail, Clock, Check, X, XCircle, Apple, Smartphone, Gift, Star, Sparkles, Search } from 'lucide-react';
import { useAuth, authHeaders } from '@/contexts/AuthContext';
import { useEventSearch } from '@/hooks/useEventSearch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MY_EVENTS_SEARCH_FIELDS = ['name', 'organizer', 'city', 'address', 'postal_code', 'department', 'description', 'disciplines', 'status'];

function StatusPill({ status }) {
  const map = {
    pending: { label: 'En attente', cls: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30', Icon: Clock },
    approved: { label: 'Validé', cls: 'bg-green-500/15 text-green-300 border-green-500/30', Icon: Check },
    rejected: { label: 'Refusé', cls: 'bg-red-500/15 text-red-300 border-red-500/30', Icon: X },
    cancelled: { label: 'Annulé', cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30', Icon: XCircle },
  };
  const s = map[status] || map.pending;
  const I = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] rounded-full border font-semibold ${s.cls}`}>
      <I className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function MonEspace() {
  const { user, token, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boostingId, setBoostingId] = useState(null);
  const [toast, setToast] = useState(null); // { kind: 'success'|'info'|'error', msg: string }

  const refreshEvents = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/my/country-events`, { headers: authHeaders(token) });
      setEvents(Array.isArray(r.data) ? r.data : []);
    } catch (_) { /* ignore */ }
  }, [token]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true, state: { from: '/mon-espace' } }); return; }
    refreshEvents().finally(() => setLoading(false));
  }, [authLoading, user, navigate, refreshEvents]);

  // Handle Stripe return from feature-boost checkout.
  useEffect(() => {
    if (authLoading || !user) return;
    const featuredId = searchParams.get('featured');
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('featured_cancel');
    if (cancelled) {
      setToast({ kind: 'info', msg: 'Paiement annulé. Aucune mise en avant n\'a été activée.' });
      setSearchParams({}, { replace: true });
      return;
    }
    if (featuredId && sessionId) {
      let attempts = 0;
      const maxAttempts = 6;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await axios.get(`${API}/country-events/feature-status/${sessionId}`);
          if (r.data?.paid) {
            setToast({ kind: 'success', msg: `🌟 « ${r.data.event_name || 'Évènement'} » est maintenant mis en avant !` });
            setSearchParams({}, { replace: true });
            await refreshEvents();
            return;
          }
        } catch (_) { /* keep polling */ }
        if (attempts < maxAttempts) {
          setTimeout(poll, 1500);
        } else {
          setToast({ kind: 'info', msg: 'Paiement en cours de vérification. Rafraîchissez la page dans quelques secondes.' });
          setSearchParams({}, { replace: true });
        }
      };
      poll();
    }
  }, [authLoading, user, searchParams, setSearchParams, refreshEvents]);

  // Auto-dismiss toast after 6s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  async function boostEvent(evt) {
    if (boostingId) return;
    setBoostingId(evt.id);
    setToast(null);
    try {
      const r = await axios.post(
        `${API}/country-events/${evt.id}/checkout-feature`,
        {},
        { headers: authHeaders(token) },
      );
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      setToast({ kind: 'error', msg: 'Impossible d\'ouvrir le paiement. Réessayez.' });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Paiement indisponible. Réessayez.';
      setToast({ kind: 'error', msg });
    } finally {
      setBoostingId(null);
    }
  }

  async function unboostEvent(evt) {
    if (!window.confirm('Retirer la mise en avant de cet évènement ? Aucun remboursement.')) return;
    try {
      await axios.post(`${API}/my/country-events/${evt.id}/unfeature`, {}, { headers: authHeaders(token) });
      await refreshEvents();
      setToast({ kind: 'info', msg: 'Mise en avant retirée.' });
    } catch (_) {
      setToast({ kind: 'error', msg: 'Impossible de retirer la mise en avant.' });
    }
  }

  // Debounced multi-field search across the user's events.
  const { query, setQuery, debouncedQuery, filtered: filteredEvents } = useEventSearch(events, MY_EVENTS_SEARCH_FIELDS);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#FFC107]" />
      </div>
    );
  }
  if (!user) return null;

  const counts = {
    pending: events.filter((e) => e.status === 'pending').length,
    approved: events.filter((e) => e.status === 'approved').length,
    rejected: events.filter((e) => e.status === 'rejected').length,
    cancelled: events.filter((e) => e.status === 'cancelled').length,
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-['Manrope']">
      <div className="fixed inset-0 -z-10 opacity-40 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 20% 0%, rgba(255,193,7,0.12), transparent 50%)' }} />

      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0D0D0D]/80 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/ball-trap-campagne" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="back-to-map">
            <ArrowLeft className="w-4 h-4" /> Ball-traps de campagne
          </Link>
          <button onClick={() => { logout(); navigate('/'); }}
            className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"
            data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-24">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-widest text-[#888] mb-1">Bonjour</p>
          <h1 className="text-3xl sm:text-4xl font-black mb-1" data-testid="user-greeting">
            {user.name || user.email.split('@')[0]}
          </h1>
          <p className="text-sm text-[#A0A0A0] inline-flex items-center gap-1.5">
            <Mail className="w-3 h-3" /> {user.email}
          </p>
        </div>

        <InstallAppBanner />

        {toast && (
          <div
            className={`mb-5 p-3 rounded-xl border text-sm flex items-start gap-2 ${
              toast.kind === 'success'
                ? 'bg-green-500/15 border-green-500/30 text-green-200'
                : toast.kind === 'error'
                ? 'bg-red-500/10 border-red-500/30 text-red-300'
                : 'bg-[#FFC107]/10 border-[#FFC107]/30 text-[#FFC107]'
            }`}
            data-testid="featured-toast"
          >
            {toast.kind === 'success' ? <Sparkles className="w-4 h-4 mt-0.5 shrink-0" /> : <Star className="w-4 h-4 mt-0.5 shrink-0" />}
            <span className="flex-1">{toast.msg}</span>
            <button onClick={() => setToast(null)} aria-label="Fermer" className="p-0.5 opacity-70 hover:opacity-100">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Quick stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="user-stats">
          <Stat label="En attente" value={counts.pending} color="text-yellow-300" />
          <Stat label="Validés" value={counts.approved} color="text-green-300" />
          <Stat label="Refusés" value={counts.rejected} color="text-red-300" />
          <Stat label="Annulés" value={counts.cancelled} color="text-gray-300" />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-bold">Mes ball-traps {events.length > 0 && <span className="text-sm font-normal text-[#888]">({filteredEvents.length}{debouncedQuery.trim() && filteredEvents.length !== events.length ? ` / ${events.length}` : ''})</span>}</h2>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {events.length > 1 && (
              <div className="relative flex-1 sm:flex-initial sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888] pointer-events-none" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher (ville, date, statut…)"
                  className="w-full pl-10 pr-9 py-2 rounded-full bg-white/[0.04] border border-white/10 focus:border-[#FFC107]/50 outline-none text-sm placeholder:text-[#666]"
                  data-testid="my-events-search-input"
                  aria-label="Rechercher dans mes évènements"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#888] hover:text-white hover:bg-white/10"
                    aria-label="Effacer"
                    data-testid="my-events-search-clear"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
            <Link to="/ball-trap-campagne/soumettre"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black text-sm font-bold hover:scale-105 transition-transform whitespace-nowrap"
              data-testid="new-event-btn">
              <Plus className="w-4 h-4" /> Nouveau
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#888] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : events.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center" data-testid="empty-state">
            <p className="text-[#C8C8C8] mb-4">Aucun ball-trap publié pour l'instant.</p>
            <Link to="/ball-trap-campagne/soumettre" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFC107] text-black font-bold text-sm">
              <Plus className="w-4 h-4" /> Publier mon premier évènement
            </Link>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center" data-testid="my-events-empty-search">
            <p className="text-[#C8C8C8] mb-4">Aucun résultat pour « <span className="text-[#FFC107]">{debouncedQuery.trim()}</span> ».</p>
            <button onClick={() => setQuery('')} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold text-sm" data-testid="my-events-reset-search">
              <X className="w-4 h-4" /> Effacer la recherche
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-events-list">
            {filteredEvents.map((evt) => {
              const today = new Date().toISOString().slice(0, 10);
              const isFeatured = !!evt.featured && (!evt.featured_until || evt.featured_until >= today);
              const canBoost = (evt.status === 'approved' || evt.status === 'pending') && !isFeatured;
              return (
              <article
                key={evt.id}
                className={`rounded-xl bg-gradient-to-br from-[#161616] to-[#0F0F0F] border transition-colors p-4 sm:p-5 ${isFeatured ? 'border-[#FFC107]/60 shadow-[0_0_24px_-12px_rgba(255,193,7,0.4)]' : 'border-white/5 hover:border-[#FFC107]/30'}`}
                data-testid={`my-event-${evt.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-white truncate">{evt.name}</h3>
                    {isFeatured && (
                      <span
                        className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[10px] rounded-full bg-[#FFC107]/20 text-[#FFC107] border border-[#FFC107]/40 font-bold uppercase tracking-wider"
                        data-testid={`featured-badge-${evt.id}`}
                      >
                        <Sparkles className="w-3 h-3" /> À la une
                      </span>
                    )}
                  </div>
                  <StatusPill status={evt.status} />
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#C8C8C8] mb-3">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{evt.date_start || '—'}{evt.date_end && ` → ${evt.date_end}`}</span>
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[evt.city, evt.postal_code, evt.department && `(${evt.department})`].filter(Boolean).join(' ')}</span>
                </div>
                {evt.status === 'rejected' && evt.rejection_reason && (
                  <p className="text-xs text-red-300/90 mb-2">⚠ {evt.rejection_reason}</p>
                )}
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {canBoost && (
                    <button
                      onClick={() => boostEvent(evt)}
                      disabled={boostingId === evt.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold hover:scale-105 transition-transform disabled:opacity-60 disabled:hover:scale-100"
                      data-testid={`boost-btn-${evt.id}`}
                      title="Affichage prioritaire sur la carte jusqu'à la fin de l'évènement"
                    >
                      {boostingId === evt.id ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Redirection…</>
                      ) : (
                        <><Sparkles className="w-3 h-3" /> Mettre en avant · 2,5 €</>
                      )}
                    </button>
                  )}
                  {isFeatured && (
                    <button
                      onClick={() => unboostEvent(evt)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-white/5 hover:bg-white/10 text-white/70 border border-white/10"
                      data-testid={`unboost-btn-${evt.id}`}
                      title="Retirer la mise en avant (aucun remboursement)"
                    >
                      <XCircle className="w-3 h-3" /> Retirer
                    </button>
                  )}
                  <Link to={`/mon-espace/event/${evt.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border ${evt.status === 'cancelled' ? 'bg-white/5 text-white/40 border-white/10 pointer-events-none opacity-50' : 'bg-white/5 hover:bg-white/10 text-white border-white/10'}`}
                    data-testid={`manage-btn-${evt.id}`}>
                    <Edit3 className="w-3 h-3" /> Gérer
                  </Link>
                </div>
              </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3 sm:p-4">
      <div className={`text-2xl font-black ${color}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-[#888] mt-0.5">{label}</div>
    </div>
  );
}

function InstallAppBanner() {
  const KEY = 'cp_install_banner_dismissed';
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(KEY) === '1'; } catch { return false; }
  });
  if (dismissed) return null;
  function dismiss() {
    try { localStorage.setItem(KEY, '1'); } catch {}
    setDismissed(true);
  }
  return (
    <div className="mb-8 rounded-2xl border border-[#FFC107]/30 bg-gradient-to-br from-[#FFC107]/10 via-[#0F0F0F] to-[#0F0F0F] p-5 relative" data-testid="install-banner">
      <button onClick={dismiss} aria-label="Masquer" className="absolute top-3 right-3 p-1 text-white/40 hover:text-white" data-testid="install-banner-dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#FFC107]/15 border border-[#FFC107]/30 text-[#FFC107] text-[11px] font-bold mb-3">
        <Gift className="w-3 h-3" /> 7 jours Pro offerts
      </div>
      <h3 className="font-black text-lg mb-1">Installez l'application ClayPulse</h3>
      <p className="text-xs text-[#C8C8C8] mb-4 max-w-md leading-relaxed">
        Suivez vos sessions, vos scores et vos statistiques en temps réel.
        Gratuit pendant 7 jours, sans engagement.
      </p>
      <div className="flex flex-wrap gap-2">
        <Link to="/ios" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold transition-colors" data-testid="install-banner-ios">
          <Apple className="w-4 h-4" /> iPhone · iPad
        </Link>
        <Link to="/android" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold transition-colors" data-testid="install-banner-android">
          <Smartphone className="w-4 h-4" /> Android (APK)
        </Link>
      </div>
    </div>
  );
}



